import type { GroupedWaveAllocation, WaveAlert, WaveAllocation } from 'types/wave/detail';

export const WAVE_CANCELLATION_ALERT_CODES = [
  'CANCEL_ORDER_ALLOCATION_ON_WAVE',
  'CANCELLED_ALLOCATION_ON_WAVE',
] as const;

export type WaveCancellationAlertCode = (typeof WAVE_CANCELLATION_ALERT_CODES)[number];

const CANCELLATION_ALERT_CODE_SET = new Set<string>(WAVE_CANCELLATION_ALERT_CODES);

export function isCancellationAlert(code?: string | null): boolean {
  return !!code && CANCELLATION_ALERT_CODE_SET.has(code);
}

export function getCancellationAlerts(alerts: WaveAlert[] = []): WaveAlert[] {
  return alerts.filter(alert => isCancellationAlert(alert.code));
}

export function getCancellationAllocationIds(alerts: WaveAlert[] = []): string[] {
  const ids = new Set<string>();

  getCancellationAlerts(alerts).forEach(alert => {
    alert.allocationIds?.forEach(id => ids.add(id));
  });

  return Array.from(ids);
}

export function flattenWaveAllocations(
  allocations: GroupedWaveAllocation[] = [],
): WaveAllocation[] {
  const flattened: WaveAllocation[] = [];

  allocations.forEach(allocation => {
    if (allocation.sameItems?.length) {
      flattened.push(...allocation.sameItems);
      return;
    }

    flattened.push(allocation);
  });

  return flattened;
}

export function getSelectableAllocationIds(record: GroupedWaveAllocation): string[] {
  if (record.sameItems?.length) {
    return record.sameItems.map(item => item.id);
  }

  return [record.id];
}

export function getAllocationsNeedingCancellation(
  alerts: WaveAlert[] = [],
  allocations: GroupedWaveAllocation[] = [],
): WaveAllocation[] {
  const cancellationIds = new Set(getCancellationAllocationIds(alerts));
  const flattened = flattenWaveAllocations(allocations);

  return flattened.filter(
    allocation =>
      cancellationIds.has(allocation.id) || isCancellationAlert(allocation.alert?.code),
  );
}

export function buildCancellationAlertMessage(
  alerts: WaveAlert[] = [],
  count: number,
): { message: string; description?: string } {
  const cancellationAlerts = getCancellationAlerts(alerts);

  const message = `${count} allocation${count === 1 ? '' : 's'} need cancellation`;

  if (cancellationAlerts.length === 1) {
    return {
      message,
      description: cancellationAlerts[0].message,
    };
  }

  if (cancellationAlerts.length > 1) {
    const orderIds = cancellationAlerts
      .map(alert => alert.orderDisplayId)
      .filter(Boolean);

    return {
      message,
      description: `Affected orders: ${orderIds.join(', ')}`,
    };
  }

  return { message };
}
